import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Play,
  Workflow,
  Users,
  BarChart3,
  ShieldCheck,
} from "lucide-react";
import "./CRM2002.scss";

// Replace this with your actual dashboard screenshot
import dashboardMock from "../../../../../assets/Images/CapitalOne.png"; 
// or use a public URL: const dashboardMock = "/images/pipeline-dashboard.png";
import CRMEUSNR from "../../../../../assets/Images/business.jpg"; // ← replace with your image path
const FEATURES = [
  {
    icon: Workflow,
    title: "Smart Pipeline Management",
    body: "Create, assign, and track deals with intelligent prioritization and automatic stage movement.",
    color: "purple",
  },
  {
    icon: Users,
    title: "Team Collaboration",
    body: "Work together in real-time, share notes, and stay on the same page across every opportunity.",
    color: "blue",
  },
  {
    icon: BarChart3,
    title: "Detailed Analytics",
    body: "Get insights and reports that refresh in real time so leadership can trust the forecast.",
    color: "green",
  },
  {
    icon: ShieldCheck,
    title: "Secure & Reliable",
    body: "Your data is 100% secure, encrypted, and always backed up with 99.95% uptime.",
    color: "orange",
  },
];

const CRM2002: React.FC = () => {
  const navigate = useNavigate();
  const goToRegister = () => navigate("/auth/signup");

  return (
    <div className="crm-page">
      {/* ========== HERO ========== */}
      <section className="crm-hero">
        <div className="crm-hero-bg" aria-hidden="true" />
        <div className="crm-hero-inner">
          {/* LEFT COPY */}
          <div className="crm-hero-copy">
            <div className="crm-badge">
              <span className="crm-badge-new">New</span>
              AI Sales Assistant is here!
              <ArrowRight size={14} />
            </div>

            <h1>
              Organize Your Pipeline.
              <br />
              <span className="crm-purple">Boost Your Revenue.</span>
            </h1>

            <p className="crm-hero-sub">
              Pipeline helps sales teams and individuals plan, track, and close
              deals with ease — one system for pipeline, forecasting, and
              automation.
            </p>

            <div className="crm-hero-cta">
              <button className="crm-btn-primary" onClick={goToRegister}>
                Get Started Free
                <ArrowRight size={16} />
              </button>
              <button className="crm-btn-outline" onClick={goToRegister}>
                <Play size={15} fill="currentColor" />
                Watch Demo
              </button>
            </div>

            <div className="crm-hero-social">
              <div className="crm-avatars">
                <span style={{ background: "#7C3AED" }} />
                <span style={{ background: "#2563EB" }} />
                <span style={{ background: "#059669" }} />
                <span style={{ background: "#D97706" }} />
              </div>
              <span>Loved by 11K+ sales teams worldwide</span>
            </div>
          </div>

          {/* RIGHT SIDE — IMAGE */}
          <div className="crm-hero-mock">
            <img
              src={dashboardMock}
              alt="Pipeline dashboard overview"
              className="crm-mock-image"
            />
          </div>
        </div>
      </section>

      {/* ========== FEATURES ========== */}
      <section className="crm-features" id="features">
        <div className="crm-section-label">FEATURES</div>
        <h2 className="crm-section-title">Everything you need in one place.</h2>

        <div className="crm-feature-grid">
          {FEATURES.map((f) => (
            <div className={`crm-feature-card crm-${f.color}`} key={f.title}>
              <div className="crm-feature-icon">
                <f.icon size={22} strokeWidth={1.75} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
    {/* ========== HOW IT WORKS ========== */}
<section className="crm-how" id="how">
  <div className="crm-how-inner">
    {/* Left visual – now uses a background image */}
    <div className="crm-how-visual">
      <div className="crm-visual-card">
        {/* Background image */}
        <img
          src={CRMEUSNR}   // ← replace with your image path
          alt="Person working on laptop"
          className="crm-visual-bg"
        />

        {/* Floating task cards (kept on top of the image) */}
        <div className="crm-floating-card crm-card-1">
          <span className="crm-fc-check">✓</span>
          Design System
          <span className="crm-fc-status done">Completed</span>
        </div>

        <div className="crm-floating-card crm-card-2">
          <span className="crm-fc-dot orange" />
          Client Review
          <span className="crm-fc-status progress">In Progress</span>
        </div>

        <div className="crm-floating-card crm-card-3">
          <span className="crm-fc-dot blue" />
          Launch Website
          <span className="crm-fc-status upcoming">Upcoming</span>
        </div>

        {/* Sticky note */}
        <div className="crm-sticky-note">
          Focus
          <br />
          Plan
          <br />
          Achieve
        </div>
      </div>
    </div>

    {/* Right steps – unchanged */}
    <div className="crm-how-copy">
      <div className="crm-section-label">HOW IT WORKS</div>
      <h2 className="crm-section-title left">
        Simple steps to
        <br />
        get more done.
      </h2>

      <div className="crm-steps">
        <div className="crm-step">
          <div className="crm-step-num">01</div>
          <div>
            <h4>Create Your Workspace</h4>
            <p>Set up your pipeline and team in seconds.</p>
          </div>
        </div>
        <div className="crm-step">
          <div className="crm-step-num">02</div>
          <div>
            <h4>Add Your Deals</h4>
            <p>Organize and prioritize what matters most.</p>
          </div>
        </div>
        <div className="crm-step">
          <div className="crm-step-num">03</div>
          <div>
            <h4>Track & Close</h4>
            <p>Stay on track and achieve your revenue goals.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>
      {/* ========== CTA BAND ========== */}
      <section className="crm-cta-band">
        <div className="crm-cta-inner">
          <h2>
            Ready to transform the way
            <br />
            you sell?
          </h2>
          <p>Join thousands of productive teams using Pipeline.</p>
          <div className="crm-cta-form">
            <input type="email" placeholder="Enter your email" />
            <button className="crm-btn-dark" onClick={goToRegister}>
              Get Started Free
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CRM2002;